import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { t } from "@/services/i18n";
import { getSupplementalsByEntity } from "@/services/supplementals";
import { marketGamesStatusOptions, marketStatusOptions, marketTypeOptions } from "../shared";

type MarketGameSlice = {
  status?: number;
  position?: number | string | null;
};

type SupplementalName = {
  supplementalNameTypeId: string | number;
  name?: string;
  eventMarketName?: string;
};

type MarketGridRow = {
  sportCode?: string;
  status?: number;
  type?: number;
  prematchGame?: MarketGameSlice;
  liveGame?: MarketGameSlice;
  feeds?: { name: string }[];
  supplementalNames?: SupplementalName[];
};

function gameStatusLabel(status: number | undefined) {
  const match = marketGamesStatusOptions.find((option) => option.value === status);
  return t(match?.title ?? "");
}

function gameStatusIcon(status: number | undefined) {
  const match = marketGamesStatusOptions.find((option) => option.value === status);
  return {
    code: match?.icon?.code ?? "",
    color: match?.icon?.color ?? "",
  };
}

function supplementalValue(
  row: MarketGridRow,
  typeId: string | number,
  field: "name" | "eventMarketName",
) {
  return (
    row.supplementalNames?.find((item) => String(item.supplementalNameTypeId) === String(typeId))?.[
      field
    ] ?? ""
  );
}

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "sportName",
    defaultVisibility: true,
    title: "table_columns.sport",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => ({ code: `${(row as MarketGridRow).sportCode ?? ""}` }),
    },
  },
  {
    key: "sportId",
    defaultVisibility: false,
    title: "table_columns.sport_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "translatedName",
    defaultVisibility: false,
    title: "table_columns.translated_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "eventMarketName",
    defaultVisibility: true,
    title: "table_columns.event_market_name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "translatedEventMarketName",
    defaultVisibility: false,
    title: "table_columns.translated_event_market_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "type",
    defaultVisibility: false,
    title: "table_columns.market_type",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const match = marketTypeOptions.find((option) => option.value === (row as MarketGridRow).type);
      return match ? t(match.title) : "";
    },
  },
  {
    key: "prematchGameStatus",
    defaultVisibility: true,
    title: "table_columns.prematch_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => gameStatusLabel((row as MarketGridRow).prematchGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as MarketGridRow).prematchGame?.status),
    },
  },
  {
    key: "prematchGamePosition",
    defaultVisibility: true,
    title: "table_columns.prematch_position",
    fieldType: FieldType.InputCell,
    apiPath: "prematchGame.position",
    sort: { enabled: true, key: "prematchGamePosition" },
    valueFormatter: (row: unknown) => {
      const position = (row as MarketGridRow).prematchGame?.position;
      return position == null ? "" : String(position);
    },
  },
  {
    key: "liveGameStatus",
    defaultVisibility: true,
    title: "table_columns.live_status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => gameStatusLabel((row as MarketGridRow).liveGame?.status),
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => gameStatusIcon((row as MarketGridRow).liveGame?.status),
    },
  },
  {
    key: "liveGamePosition",
    defaultVisibility: true,
    title: "table_columns.live_position",
    fieldType: FieldType.InputCell,
    apiPath: "liveGame.position",
    sort: { enabled: true, key: "liveGamePosition" },
    valueFormatter: (row: unknown) => {
      const position = (row as MarketGridRow).liveGame?.position;
      return position == null ? "" : String(position);
    },
  },
  {
    key: "feeds",
    defaultVisibility: false,
    title: "table_columns.feeds",
    fieldType: FieldType.ArrayCell,
    valueFormatter: (row: unknown) =>
      ((row as MarketGridRow).feeds ?? []).map((feed) => feed.name?.split(" - ")[0] ?? ""),
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = marketStatusOptions.find((option) => option.value === (row as MarketGridRow).status);
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = marketStatusOptions.find(
          (option) => option.value === (row as MarketGridRow).status,
        );
        return { code: status?.icon?.code ?? "", color: status?.icon?.color ?? "" };
      },
    },
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [{ icon: "table-d", label: "Column view" }],
    rowActions: [
      { icon: "edit-ng", label: "Edit", onClick: (ctx) => ctx.openDialog("edit") },
      { icon: "trash-ng", label: "Delete", onClick: (ctx) => ctx.openDialog("delete") },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () =>
      getSupplementalsByEntity("MARKET").map((item) => ({
        key: `supplementalName_${item.id}_name`,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) => supplementalValue(row as MarketGridRow, item.id, "name"),
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      })),
  },
  {
    anchor: "eventMarketName",
    createColumns: () =>
      getSupplementalsByEntity("MARKET").map((item) => ({
        key: `supplementalName_${item.id}_eventMarketName`,
        title: `table_columns.${item.name.toLowerCase()}_event_market_name`,
        fieldType: FieldType.InputCell,
        defaultVisibility: false,
        valueFormatter: (row: unknown) =>
          supplementalValue(row as MarketGridRow, item.id, "eventMarketName"),
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, eventMarketName: value }],
        }),
      })),
  },
];
